import mammoth from 'mammoth';
import { extractImageMeta, type ImageMeta } from './imageMeta';
import { extractHeaderFooter } from './headerFooterRead';
import { extractComments } from './commentsRead';
import { extractTrackChanges, type TrackedRun } from './trackChangesRead';
import type { DocComment } from '@/state/types';

// ═══════════════════════════════════════════════════════════════
// Import DOCX → HTML (mammoth.js), sekce 2.4
//
// Nad rámec výchozího chování mammoth přenášíme tři věci, které mammoth
// jinak zahazuje a které jsou pro věrnost důležité:
//
//   1) ZAROVNÁNÍ ODSTAVCE (w:jc) — transformem na syntetický styleName,
//      style map → CSS třída, post-process → inline `text-align`.
//
//   2) FONT (w:rFonts) a 3) VELIKOST PÍSMA (w:sz) — mammoth je na runu zná
//      (`run.font`, `run.fontSize`), ale do HTML je nepromítá. Řešíme je
//      společně: run transform zakóduje font+velikost do jednoho
//      syntetického styleName, dvouprůchodově vygenerujeme style map (první
//      průchod posbírá jména), a v post-processingu je dekódujeme zpět na
//      inline `font-family` a `font-size`.
//
// Nadpisové styly (Heading 1–6, Title, Subtitle) → <h1>–<h6>.
// ═══════════════════════════════════════════════════════════════

// Kódování font+velikost do bezpečného názvu třídy (hex JSON).
function encodeRun(font: string | null, size: number | null): string {
  return 'ttrun_' + toHex(JSON.stringify({ f: font || '', s: size || 0 }));
}
function decodeRun(cls: string): { f: string; s: number } | null {
  if (!cls.startsWith('ttrun_')) return null;
  try {
    return JSON.parse(fromHex(cls.slice('ttrun_'.length)));
  } catch {
    return null;
  }
}
function toHex(s: string): string {
  return Array.from(new TextEncoder().encode(s)).map((b) => b.toString(16).padStart(2, '0')).join('');
}
function fromHex(h: string): string {
  const bytes = new Uint8Array(h.match(/.{1,2}/g)!.map((x) => parseInt(x, 16)));
  return new TextDecoder().decode(bytes);
}

const BASE_STYLE_MAP = [
  "p[style-name='Title'] => h1.doc-title:fresh",
  "p[style-name='Subtitle'] => h2.doc-subtitle:fresh",
  "p[style-name='Heading 1'] => h1:fresh",
  "p[style-name='Heading 2'] => h2:fresh",
  "p[style-name='Heading 3'] => h3:fresh",
  "p[style-name='Heading 4'] => h4:fresh",
  "p[style-name='Heading 5'] => h5:fresh",
  "p[style-name='Heading 6'] => h6:fresh",
  'r[style-name="Strong"] => strong',
  'r[style-name="Emphasis"] => em',
  "p[style-name='align-center'] => p.tt-center:fresh",
  "p[style-name='align-right'] => p.tt-right:fresh",
  "p[style-name='align-justify'] => p.tt-justify:fresh",
  "p[style-name='align-both'] => p.tt-justify:fresh",
];

export interface ImportResult {
  html: string;
  messages: string[];
  name: string;
  headerHtml?: string;
  footerHtml?: string;
  /** Komentáře načtené z OOXML (Fáze 3). */
  comments?: DocComment[];
}

// Paragraph transform: zarovnání → syntetický styleName.
function paragraphTransform(p: any) {
  if (p.alignment && !p.styleName) {
    const a = p.alignment === 'both' ? 'justify' : p.alignment;
    return { ...p, styleName: 'align-' + a };
  }
  return p;
}

// Run transform: font+velikost → kombinovaný syntetický styleName.
function runTransform(r: any) {
  if ((r.font || r.fontSize) && !r.styleName) {
    return { ...r, styleName: encodeRun(r.font, r.fontSize) };
  }
  return r;
}

// Post-process: tt-* třídy → text-align; ttrun_* třídy → font-family/size;
// obohacení <img> o rozměry a obtékání z OOXML metadat.
function postProcess(
  html: string,
  imageMeta: ImageMeta[] = [],
  rangeText: Record<number, string> = {},
  trackedRuns: TrackedRun[] = [],
): string {
  const dom = new DOMParser().parseFromString(html, 'text/html');

  const alignMap: Record<string, string> = {
    'tt-center': 'center', 'tt-right': 'right', 'tt-justify': 'justify', 'tt-left': 'left',
  };
  dom.querySelectorAll('[class]').forEach((el) => {
    const e = el as HTMLElement;
    // Zarovnání
    for (const [cls, val] of Object.entries(alignMap)) {
      if (e.classList.contains(cls)) { e.style.textAlign = val; e.classList.remove(cls); }
    }
    // Font + velikost (kombinovaná třída)
    e.classList.forEach((cls) => {
      const dec = decodeRun(cls);
      if (dec) {
        if (dec.f) e.style.fontFamily = dec.f;
        if (dec.s) e.style.fontSize = dec.s + 'pt';
        e.classList.remove(cls);
      }
    });
    if (!e.getAttribute('class')) e.removeAttribute('class');
  });

  // Obrázky — napáruj metadata z OOXML na <img> v pořadí výskytu.
  if (imageMeta.length) {
    const imgs = dom.querySelectorAll('img');
    imgs.forEach((img, i) => {
      const meta = imageMeta[i];
      if (!meta) return;
      if (meta.width) img.setAttribute('width', String(meta.width));
      if (meta.height) img.setAttribute('height', String(meta.height));
      img.setAttribute('data-wrap', meta.wrap);
      img.setAttribute('data-align', meta.align);
    });
  }

  // Komentáře (Fáze 3) — mammoth commentRangeStart/End nevystavuje, takže
  // pasáž najdeme v textu podle řetězce vytaženého z document.xml a obalíme
  // ji markem <span data-comment-id>. Když se text nenajde (např. rozsah přes
  // víc odstavců nebo formátování rozseklo text), komentář zůstane v panelu
  // bez vazby na text — což je lepší než ho zahodit.
  for (const [idStr, text] of Object.entries(rangeText)) {
    if (!text) continue;
    wrapTextInComment(dom, text, parseInt(idStr, 10));
  }

  // Sledované změny — vložený text v HTML označíme, smazaný do HTML vložíme
  // (mammoth ho zahodil, takže ho tam vůbec není).
  applyTrackedRuns(dom, trackedRuns);

  return dom.body.innerHTML;
}

/**
 * Promítne sledované změny do naimportovaného HTML.
 *
 * Vložený text (ins) mammoth propustil — najdeme ho a obalíme značkou.
 * Smazaný text (del) mammoth ZAHODIL — musíme ho do HTML vložit sami, jinak
 * by změna zmizela a nešla by odmítnout ani exportovat zpět do Wordu.
 *
 * Umístění řešíme přes KONTEXT z OOXML: u každé změny víme, jaký text jí
 * v odstavci předchází (`before`). Ten text v HTML najdeme a změnu vložíme
 * hned za něj. Bez kontextu by se smazaný text musel umísťovat odhadem —
 * a to nefungovalo (končil na špatném místě, nebo v dokumentu vůbec nebyl).
 */
function applyTrackedRuns(dom: Document, runs: TrackedRun[]): void {
  if (!runs.length) return;
  // Od konce, aby vkládání neposouvalo kotvy dřívějších změn.
  for (const run of [...runs].sort((a, b) => b.order - a.order)) {
    if (run.kind === 'ins') {
      wrapTextInTrack(dom, run);
    } else {
      insertDeletedText(dom, run);
    }
  }
}

/** Vytvoří span značky sledované změny. */
function trackSpan(dom: Document, run: TrackedRun): HTMLElement {
  const span = dom.createElement('span');
  span.setAttribute(run.kind === 'ins' ? 'data-track-ins' : 'data-track-del', '');
  span.setAttribute('data-author', run.author);
  span.setAttribute('data-date', run.date);
  span.textContent = run.text;
  return span;
}

/**
 * Najde v HTML pozici, kam změna patří — konec textu `before` v odstavci,
 * který změnu obsahuje. Vrací textový uzel a offset v něm.
 */
function findAnchor(dom: Document, run: TrackedRun): { node: Text; offset: number } | null {
  // Odstavce prohledáváme jeden po druhém: hledáme ten, jehož text začíná
  // kontextem `before` (a případně obsahuje i `after`).
  const blocks = dom.body.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, td, th');
  const candidates: Element[] = blocks.length ? Array.from(blocks) : [dom.body];

  for (const block of candidates) {
    const blockText = block.textContent || '';
    // Kotva: konec `before` v tomto bloku. Prázdný before = začátek bloku.
    if (run.before && !blockText.startsWith(run.before)) continue;
    // Když je kontext prázdný, ověř aspoň, že blok navazuje textem `after`.
    if (!run.before && run.after && !blockText.startsWith(run.after)) continue;

    const targetOffset = run.before.length;
    // Najdi textový uzel, ve kterém tenhle offset leží.
    const walker = dom.createTreeWalker(block, NodeFilter.SHOW_TEXT);
    let seen = 0;
    let n: Node | null;
    while ((n = walker.nextNode())) {
      const t = n as Text;
      const len = (t.textContent || '').length;
      if (seen + len >= targetOffset) {
        return { node: t, offset: targetOffset - seen };
      }
      seen += len;
    }
    // Kontext končí přesně na konci bloku.
    const last = block.lastChild;
    if (last && last.nodeType === 3) {
      return { node: last as Text, offset: (last.textContent || '').length };
    }
  }
  return null;
}

/** Najde vložený text podle kontextu a obalí ho značkou insertion. */
function wrapTextInTrack(dom: Document, run: TrackedRun): boolean {
  const anchor = findAnchor(dom, run);
  if (!anchor) return false;

  const { node, offset } = anchor;
  const content = node.textContent || '';
  // Text vloženého úseku musí na kotvě reálně být.
  if (!content.startsWith(run.text, offset)) return false;
  // Už označené? Nepřidávej znovu.
  if (node.parentElement?.closest('[data-track-ins]')) return true;

  const span = trackSpan(dom, run);
  const after = node.splitText(offset);
  after.splitText(run.text.length);
  after.parentNode?.replaceChild(span, after);
  return true;
}

/**
 * Vloží smazaný text zpět do HTML (mammoth ho zahodil) na místo dané
 * kontextem a označí ho jako deletion.
 */
function insertDeletedText(dom: Document, run: TrackedRun): void {
  const span = trackSpan(dom, run);
  const anchor = findAnchor(dom, run);

  if (anchor) {
    const { node, offset } = anchor;
    const rest = node.splitText(offset);
    rest.parentNode?.insertBefore(span, rest);
    return;
  }

  // Kotva nenalezena (např. rozsah přes víc odstavců) — radši text neztratit
  // a přidat ho na konec posledního odstavce, než ho zahodit.
  const paras = dom.body.querySelectorAll('p');
  const target = paras.length ? paras[paras.length - 1] : dom.body;
  target.appendChild(span);
}

/**
 * Najde v dokumentu první výskyt daného textu a obalí ho commentMark spanem.
 * Prochází textové uzly; hledá jen v rámci jednoho textového uzlu (rozsahy
 * přes hranice formátování se nepodaří označit — přijatelný kompromis).
 */
function wrapTextInComment(dom: Document, text: string, commentId: number): boolean {
  if (isNaN(commentId)) return false;
  const walker = dom.createTreeWalker(dom.body, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];
  let n: Node | null;
  while ((n = walker.nextNode())) nodes.push(n as Text);

  for (const node of nodes) {
    const content = node.textContent || '';
    const idx = content.indexOf(text);
    if (idx === -1) continue;
    // Už je uvnitř jiného komentáře? Vnořování zvládneme, ale nechceme
    // označit tentýž rozsah dvakrát stejným ID.
    const parentEl = node.parentElement;
    if (parentEl?.closest(`[data-comment-id="${commentId}"]`)) return true;

    const span = dom.createElement('span');
    span.setAttribute('data-comment-id', String(commentId));
    span.textContent = text;

    const after = node.splitText(idx);
    after.splitText(text.length); // odděl zbytek za pasáží
    after.parentNode?.replaceChild(span, after);
    return true;
  }
  return false;
}

export async function importDocx(buffer: ArrayBuffer, fileName: string): Promise<ImportResult> {
  const transformDocument = (el: any) => runTransform(paragraphTransform(el));

  // 1. průchod — posbírej syntetická jména runů (font+velikost), abychom
  //    z nich mohli vygenerovat style map (mammoth potřebuje jména předem).
  const runNames = new Set<string>();
  await mammoth.convertToHtml(
    { arrayBuffer: buffer.slice(0) },
    {
      transformDocument: (mammoth as any).transforms.run((r: any) => {
        if (r.font || r.fontSize) runNames.add(encodeRun(r.font, r.fontSize));
        return r;
      }),
    },
  );

  const styleMap = [
    ...BASE_STYLE_MAP,
    ...[...runNames].map((n) => `r[style-name='${n}'] => span.${n}`),
  ];

  // 2. průchod — reálná konverze s plnou style map a transformy.
  const result = await mammoth.convertToHtml(
    { arrayBuffer: buffer },
    {
      styleMap,
      transformDocument,
      // Obrázky (Blok 5): mammoth vrací base64 data URL. Rozměry (w:ext) ani
      // režim obtékání (wp:anchor) mammoth ve svém image callbacku nevystavuje,
      // proto se importují jako inline v přirozené velikosti — uživatel je pak
      // může zvětšit/zmenšit a nastavit obtékání přes ovládací panel obrázku.
      convertImage: mammoth.images.imgElement(async (image) => {
        const buf = await image.read('base64');
        // POZOR: mammoth zapisuje atributy do HTML a volá na jejich hodnotách
        // .replace(); hodnota `undefined` proto shodí celý import
        // (escapeHtmlAttribute → undefined.replace). Nikdy tedy nevkládáme
        // atribut s undefined hodnotou — přidáme `alt` jen když reálně je.
        const alt = (image as any).altText as string | undefined;
        return {
          src: `data:${image.contentType};base64,${buf}`,
          'data-wrap': 'inline',
          ...(alt ? { alt } : {}),
        };
      }),
    },
  );

  // Metadata obrázků (rozměry + obtékání) čteme přímo z OOXML paralelně —
  // mammoth je nevystavuje. Napárujeme je na <img> v post-processingu.
  const imageMeta = await extractImageMeta(buffer.slice(0));

  // Záhlaví/zápatí čteme také z OOXML (mammoth je ignoruje úplně).
  const { headerHtml, footerHtml } = await extractHeaderFooter(buffer.slice(0));

  // Komentáře (Fáze 3) — mammoth je zahazuje, čteme je z OOXML sami.
  const { comments, rangeText } = await extractComments(buffer.slice(0));

  // Sledované změny (Fáze 3) — mammoth vložený text propustí bez značky
  // a smazaný zahodí úplně, proto je čteme z OOXML sami.
  const { runs: trackedRuns } = await extractTrackChanges(buffer.slice(0));

  return {
    html: postProcess(result.value || '<p></p>', imageMeta, rangeText, trackedRuns) || '<p></p>',
    messages: result.messages.map((m) => `${m.type}: ${m.message}`),
    name: fileName.replace(/\.docx$/i, ''),
    headerHtml,
    footerHtml,
    comments,
  };
}
